import { useEffect, useMemo, useState } from 'react';
import { Container, Typography, Box, TextField, MenuItem, Button, Table, TableHead, TableRow, TableCell, TableBody, Chip, Stack } from '@mui/material';
import { getFinancialSummary, exportFinancialFile } from '../api/reportsApi';

export default function Financials() {
  const [groupBy, setGroupBy] = useState<'driver'|'company'|'month'|'truck'>('driver');
  const [start, setStart] = useState<string>('');
  const [end, setEnd] = useState<string>('');
  const [rows, setRows] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await getFinancialSummary({ group_by: groupBy, start: start || undefined, end: end || undefined });
      setRows(res.rows || []);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Erreur lors du chargement des rapports');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const totals = useMemo(() => {
    return rows.reduce((acc, r) => {
      acc.revenue += r.totalRevenue || 0;
      acc.tripExp += r.totals?.tripExpensesTotal || 0;
      acc.globExp += r.totals?.globalExpensesTotal || 0;
      acc.net += r.totals?.net || 0;
      return acc;
    }, { revenue: 0, tripExp: 0, globExp: 0, net: 0 });
  }, [rows]);

  const handleExport = async (format: 'csv'|'xlsx'|'pdf') => {
    try {
      await exportFinancialFile({ 
        group_by: groupBy, 
        start: start || undefined, 
        end: end || undefined 
      }, format);
      // Le téléchargement se fait automatiquement, pas besoin de message
    } catch (e: any) {
      setError(e?.message || 'Erreur lors de l\'export');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Typography variant="h4" gutterBottom>Rapports Financiers</Typography>
      <Box mb={2} display="grid" gridTemplateColumns={{ xs: '1fr', md: 'repeat(6, 1fr)' }} gap={2}>
        <TextField select label="Grouper par" value={groupBy} onChange={(e) => setGroupBy(e.target.value as any)}>
          <MenuItem value="driver">Chauffeur</MenuItem>
          <MenuItem value="company">Compagnie</MenuItem>
          <MenuItem value="truck">Camion</MenuItem>
          <MenuItem value="month">Mois</MenuItem>
        </TextField>
        <TextField type="date" label="Début" InputLabelProps={{ shrink: true }} value={start} onChange={(e) => setStart(e.target.value)} />
        <TextField type="date" label="Fin" InputLabelProps={{ shrink: true }} value={end} onChange={(e) => setEnd(e.target.value)} />
        <Button variant="contained" onClick={load} disabled={loading}>{loading ? 'Chargement...' : 'Filtrer'}</Button>
        <Button variant="outlined" onClick={() => handleExport('csv')}>Export CSV</Button>
        <Button variant="outlined" onClick={() => handleExport('xlsx')}>Export Excel</Button>
        <Button variant="outlined" onClick={() => handleExport('pdf')}>Export PDF</Button>
      </Box>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Groupe</TableCell>
            <TableCell align="right">Recettes</TableCell>
            <TableCell align="right">Dépenses (courses)</TableCell>
            <TableCell align="right">Dépenses (globales)</TableCell>
            <TableCell align="right">Net</TableCell>
            <TableCell>Breakdown (dépenses)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={String(r.groupId)}>
              <TableCell>{r.groupName}</TableCell>
              <TableCell align="right">{r.totalRevenue?.toLocaleString?.() ?? r.totalRevenue}</TableCell>
              <TableCell align="right">{r.totals?.tripExpensesTotal?.toLocaleString?.() ?? r.totals?.tripExpensesTotal}</TableCell>
              <TableCell align="right">{r.totals?.globalExpensesTotal?.toLocaleString?.() ?? r.totals?.globalExpensesTotal}</TableCell>
              <TableCell align="right">{r.totals?.net?.toLocaleString?.() ?? r.totals?.net}</TableCell>
              <TableCell>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {Object.entries({ ...(r.tripExpenses||{}), ...(r.globalExpenses||{}) }).map(([k, v]) => (
                    <Chip key={k} label={`${k}:${v}`} size="small" />
                  ))}
                </Stack>
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell>Total</TableCell>
            <TableCell align="right">{totals.revenue.toLocaleString?.() ?? totals.revenue}</TableCell>
            <TableCell align="right">{totals.tripExp.toLocaleString?.() ?? totals.tripExp}</TableCell>
            <TableCell align="right">{totals.globExp.toLocaleString?.() ?? totals.globExp}</TableCell>
            <TableCell align="right">{totals.net.toLocaleString?.() ?? totals.net}</TableCell>
            <TableCell />
          </TableRow>
        </TableBody>
      </Table>
    </Container>
  );
}